/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The location leg of a path type-down, for any tree of paths** (`#1249`, shared by `#1580`).
 *
 * `getSchemaSuggestions` reads a query as a **location** where it names one (*show me what is here*)
 * and as a **search** where it does not (*find me this wherever it lives*). That rule was written
 * against the session schema. A request strategy's round targets (`strategy.floorCpm`,
 * `request.headers.X-Foo`) are another tree with the same question, so the rule is stated once, here,
 * over a tree the caller describes, and each type-down supplies its own search leg.
 *
 * | The query | Reads as | Offers |
 * | --- | --- | --- |
 * | empty | the top of the tree | its roots |
 * | names a branch (`session`, `request.`) | that location | its children, one segment deep |
 * | a partial segment under one (`session.con`) | that location, filtered | the siblings matching |
 * | anything else | not a location: `null` | the caller's search |
 */

import type { CatalogSuggestion } from './data-model-schema';

export interface PathTree {
  /** The segments directly under `base` (`''` is the top), or `null` where `base` is not a branch. */
  childrenOf: (base: string) => readonly string[] | null;
  /** The rows offered for one path. */
  rowsFor: (path: string) => CatalogSuggestion[];
  /**
   * What a branch whose keys are arbitrary offers instead of children, or `null` where `base` is not
   * one (`CONTEXT_ROOTS`' `open`). Asked before the children, as an open root has none to list.
   */
  openBranch: (base: string) => CatalogSuggestion[] | null;
}

/**
 * The location leg: what is *here*, where the query names a here. Returns `null` when the query is not
 * a location, which sends `max` and `{ip}` to the caller's search. A query ending in `.` is always read
 * as a location: `sess.` names nowhere and answers with nothing, rather than a search for `sess.` that
 * would also find nothing while looking like it tried.
 */
export function locationIn(tree: PathTree, query: string): CatalogSuggestion[] | null {
  const trailingDot = query.endsWith('.');
  const stripped = trailingDot ? query.slice(0, -1) : query;

  // Empty is the top of the tree, and its children are the roots.
  if (!stripped) return trailingDot ? null : offerChildren(tree, '', '');

  if (tree.childrenOf(stripped) !== null) return offerChildren(tree, stripped, '');
  if (trailingDot) return offerChildren(tree, stripped, '', true);

  // A partial segment under a location: everything before the last `.` has to name a branch, or this
  // is not a location at all and the search leg is the right answer.
  const cut = stripped.lastIndexOf('.');
  if (cut === -1) return null;
  const base = stripped.slice(0, cut);
  const partial = stripped.slice(cut + 1);
  if (tree.childrenOf(base) === null) return null;
  return offerChildren(tree, base, partial);
}

/**
 * The children of one location, filtered by the segment being typed. `exhausted` is the *named a
 * location that has no children* case, distinguished from a location with children none of which
 * match: the first is a fact about the tree, the second about the query.
 */
function offerChildren(tree: PathTree, base: string, partial: string, exhausted = false): CatalogSuggestion[] {
  const open = base ? tree.openBranch(base) : null;
  if (open) return open;
  if (exhausted) return [];

  const normalizedPartial = normalize(partial);
  return [...(tree.childrenOf(base) ?? [])]
    .filter((segment) => !normalizedPartial || normalize(segment).includes(normalizedPartial))
    .sort((a, b) => {
      const aStarts = normalize(a).startsWith(normalizedPartial);
      const bStarts = normalize(b).startsWith(normalizedPartial);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      return a.localeCompare(b);
    })
    .flatMap((segment) => tree.rowsFor(base ? `${base}.${segment}` : segment));
}

/** Case, spaces, `_` and `-` are not what an operator means by a segment: `pubgenre` matches `pub_genre`. */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[\s_-]/g, '');
}
